import { useState, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { collection, onSnapshot, doc, setDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
    initializeProjectOrder,
    initializeUserProjectSettings,
    initializeUserTodos
} from '../utils/firebaseUtils';

export const useDashboardData = (user) => {
    const [projects, setProjects] = useLocalStorage('dashboardProjects', []);
    const [projectOrder, setProjectOrder] = useLocalStorage('dashboardProjectOrder', []);
    const [isLoading, setIsLoading] = useState(() => {
        // Don't show loading when cached project data is available from useLocalStorage
        return !projects || projects.length === 0;
    });
    const [globalTodos, setGlobalTodos] = useLocalStorage('dashboardGlobalTodos', []);
    const [starredProjectIds, setStarredProjectIds] = useLocalStorage('dashboardStarredProjectIds', []);
    const [workspaces, setWorkspaces] = useLocalStorage('dashboardWorkspaces', []);
    const [projectsByWorkspace, setProjectsByWorkspace] = useState({});
    const [unassignedProjects, setUnassignedProjects] = useState([]);
    const [workspaceProjectOrders, setWorkspaceProjectOrders] = useState({});
    const [unassignedProjectOrder, setUnassignedProjectOrder] = useState([]);

    // Initialize unassigned project order
    useEffect(() => {
        const unassignedOrderRef = doc(db, 'settings', 'unassignedProjectOrder');
        const unsubscribe = onSnapshot(unassignedOrderRef, (snapshot) => {
            if (snapshot.exists()) {
                setUnassignedProjectOrder(snapshot.data().order || []);
            } else {
                setDoc(unassignedOrderRef, {
                    order: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });
            }
        });

        return () => unsubscribe();
    }, []);

    // Initialize workspace project orders
    useEffect(() => {
        if (!workspaces.length) return;

        const unsubscribes = workspaces.map(workspace =>
            onSnapshot(doc(db, 'workspaces', workspace.id), (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setWorkspaceProjectOrders(prev => ({
                        ...prev,
                        [workspace.id]: data.projectOrder || []
                    }));
                }
            })
        );

        return () => unsubscribes.forEach(unsubscribe => unsubscribe());
    }, [workspaces]);

    // Initialize dashboard data
    useEffect(() => {
        if (!user) {
            setIsLoading(false);
            return;
        }

        // Only show loading state if we don't have cached data
        const hasCachedProjects = projects.length > 0;
        if (!hasCachedProjects) {
            setIsLoading(true);
        }

        const unsubscribes = [];

        const fetchData = async () => {
            try {
                // Initialize project order and user settings
                const orderDocRef = doc(db, 'settings', 'projectOrder');
                const order = await initializeProjectOrder(orderDocRef);
                setProjectOrder(order);

                const initialUserSettings = await initializeUserProjectSettings(user.uid);
                setStarredProjectIds(initialUserSettings.starredProjects || []);

                let projectsLoaded = false;
                let workspacesLoaded = false;

                const checkLoadingStatus = () => {
                    if (projectsLoaded && workspacesLoaded) {
                        setIsLoading(false);
                    }
                };

                // Set up listeners
                const unsubscribeProjects = onSnapshot(
                    collection(db, 'projects'),
                    (snapshot) => {
                        const projectsData = snapshot.docs
                            .map(doc => ({
                                id: doc.id,
                                ...doc.data()
                            }))
                            .filter(project => !project.archived);

                        setProjects(projectsData);
                        projectsLoaded = true;
                        checkLoadingStatus();
                    }, (error) => {
                        console.error("Error fetching projects:", error);
                        projectsLoaded = true;
                        checkLoadingStatus();
                    });
                unsubscribes.push(unsubscribeProjects);

                const workspacesQuery = query(
                    collection(db, 'workspaces'),
                    where(`members.${user.uid}`, '!=', null)
                );
                const unsubscribeWorkspaces = onSnapshot(workspacesQuery, (snapshot) => {
                    const workspacesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setWorkspaces(workspacesData);
                    workspacesLoaded = true;
                    checkLoadingStatus();
                }, (error) => {
                    console.error("Error fetching workspaces:", error);
                    workspacesLoaded = true;
                    checkLoadingStatus();
                });
                unsubscribes.push(unsubscribeWorkspaces);


                const unsubscribeUserSettings = onSnapshot(
                    doc(db, 'userSettings', user.uid),
                    (doc) => {
                        if (doc.exists()) {
                            setStarredProjectIds(doc.data().starredProjects || []);
                        }
                    }
                );
                unsubscribes.push(unsubscribeUserSettings);

                const userTodosRef = doc(db, 'userTodos', user.uid);
                const unsubscribeUserTodos = onSnapshot(
                    userTodosRef,
                    async (doc) => {
                        if (doc.exists()) {
                            setGlobalTodos(doc.data().items || []);
                        } else {
                            const initialTodos = await initializeUserTodos(user.uid);
                            setGlobalTodos(initialTodos);
                        }
                    }
                );
                unsubscribes.push(unsubscribeUserTodos);

                const unsubscribeOrder = onSnapshot(
                    doc(db, 'settings', 'projectOrder'),
                    (doc) => {
                        if (doc.exists()) {
                            setProjectOrder(doc.data().order || []);
                        }
                    }
                );
                unsubscribes.push(unsubscribeOrder);

            } catch (error) {
                console.error('Error initializing dashboard:', error);
                setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, [user]);

    // Process projects
    useEffect(() => {
        if (!projects.length) return;

        const byWorkspace = {};
        const unassigned = [];

        projects.forEach(project => {
            if (starredProjectIds.includes(project.id)) return;

            const hasDirectAccess = project.members?.[user.uid] !== undefined;
            const hasWorkspaceAccess = project.workspaceId &&
                workspaces.some(w => w.id === project.workspaceId);

            if (!hasDirectAccess && !hasWorkspaceAccess) return;

            if (project.workspaceId) {
                if (!byWorkspace[project.workspaceId]) {
                    byWorkspace[project.workspaceId] = [];
                }
                byWorkspace[project.workspaceId].push({
                    ...project,
                    uniqueId: `${project.id}-workspace-${project.workspaceId}`
                });
            } else {
                unassigned.push({
                    ...project,
                    uniqueId: `${project.id}-unassigned`
                });
            }
        });

        // Sort workspace projects
        Object.keys(byWorkspace).forEach(workspaceId => {
            const order = workspaceProjectOrders[workspaceId] || [];
            byWorkspace[workspaceId].sort((a, b) => {
                const indexA = order.indexOf(a.id);
                const indexB = order.indexOf(b.id);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
        });

        // Sort unassigned projects
        const sortedUnassigned = [...unassigned].sort((a, b) => {
            const indexA = unassignedProjectOrder.indexOf(a.id);
            const indexB = unassignedProjectOrder.indexOf(b.id);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        setProjectsByWorkspace(byWorkspace);
        setUnassignedProjects(sortedUnassigned);
    }, [projects, workspaces, workspaceProjectOrders, unassignedProjectOrder, starredProjectIds, user?.uid]);

    return {
        projects,
        projectOrder,
        setProjectOrder,
        isLoading,
        globalTodos,
        setGlobalTodos,
        starredProjectIds,
        workspaces,
        projectsByWorkspace,
        unassignedProjects,
        unassignedProjectOrder,
        setUnassignedProjectOrder,
        workspaceProjectOrders
    };
};
